// Full screen overlay component
// Customized Dialog component for full screen overlays (e.g. RoomConnectionOverlay)

import * as React from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { cn } from '@/utilities/cn';
import {
  createDialogPresenceId,
  registerOpenDialog,
  type DialogPresenceId,
} from '@/utilities/dialogPresenceStore';

type FullScreenOverlayProps = {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  title: React.ReactNode;
  description: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  pauseBackground?: boolean;
  onEscapeKeyDown?: React.ComponentPropsWithoutRef<
    typeof DialogPrimitive.Content
  >['onEscapeKeyDown'];
  onPointerDownOutside?: React.ComponentPropsWithoutRef<
    typeof DialogPrimitive.Content
  >['onPointerDownOutside'];
};

function FullScreenOverlay({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
  pauseBackground = true,
  onEscapeKeyDown,
  onPointerDownOutside,
}: FullScreenOverlayProps) {
  const dialogPresenceIdRef = React.useRef<DialogPresenceId | null>(null);
  if (dialogPresenceIdRef.current === null) {
    dialogPresenceIdRef.current = createDialogPresenceId();
  }
  const dialogPresenceId = dialogPresenceIdRef.current;

  React.useLayoutEffect(() => {
    if (!open) {
      return;
    }
    return registerOpenDialog(dialogPresenceId, { pauseBackground });
  }, [dialogPresenceId, open, pauseBackground]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 isolate z-[3000] bg-background duration-0 data-open:animate-none data-closed:animate-none'
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-0 z-[3000] grid h-svh w-svw place-items-center overflow-auto bg-background p-0 text-foreground outline-none duration-0',
            className
          )}
          onEscapeKeyDown={onEscapeKeyDown}
          onPointerDownOutside={onPointerDownOutside}
        >
          <DialogPrimitive.Title className="sr-only">
            {title}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {description}
          </DialogPrimitive.Description>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export { FullScreenOverlay };
